import React, { useContext, useEffect, useState } from "react";
import { CSVLink } from "react-csv";
import { Table, Container } from "react-bootstrap";
import axios from "axios";
import { Store } from "../../Store";

const AdminStats = () => {
  const { state } = useContext(Store);
  const { userInfo, baseUrl } = state;

  const [csvData, setCsvData] = useState([]);
  const [Orders, setOrders] = useState([]);
  const [totalFunds, setTotalFunds] = useState(0);

  const [stats, setStats] = useState({
    totalOrders: { length: 0, value: 0 },
    completedOrders: { length: 0, value: 0 },
    rejectedOrders: { length: 0, value: 0 },
    ordersToday: { length: 0, value: 0 },
    ordersThisWeek: { length: 0, value: 0 },
    ordersThisMonth: { length: 0, value: 0 },
    ordersLastMonth: { length: 0, value: 0 },
  });

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        const { data } = await axios.get(`${baseUrl}/order/allorders`, {
          headers: { Authorization: `Bearer ${userInfo.token}` },
        });
        setOrders(data.sort((a, b) => new Date(b.date) - new Date(a.date)));
      } catch (error) {
        console.log("Error fetching transactions:", error);
      }
    };
    fetchOrders();
  }, [userInfo, baseUrl]);

  //Fetch user data from BE
  useEffect(() => {
    async function fetchUsers() {
      try {
        const { data } = await axios.get(`${baseUrl}/user/account-balances`, {
          headers: { Authorization: `Bearer ${userInfo.token}` },
        });
        const total = data.reduce((sum, user) => sum + (user.balance || 0), 0);
        setTotalFunds(total);
        // setUsers(data);
      } catch (error) {
        console.error("Error fetching users:", error);
      }
    }
    fetchUsers();
  }, [userInfo, baseUrl]);

  useEffect(() => {
    const parseDate = (dateString) => {
      const [datePart, timePart] = dateString.split(", ");
      const [day, month, year] = datePart.split("/").map(Number);
      const [hours, minutes, seconds] = timePart.split(":").map(Number);
      return new Date(year, month - 1, day, hours, minutes, seconds);
    };

    const monthNames = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];

    const calculateStats = (orders) => {
      const today = new Date();
      const startOfWeek = new Date(today);
      startOfWeek.setDate(today.getDate() - today.getDay());
      const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
      const startOfLastMonth = new Date(
        today.getFullYear(),
        today.getMonth() - 1,
        1
      );
      const endOfLastMonth = new Date(today.getFullYear(), today.getMonth(), 0);

      const totalOrders = orders.length;
      const totalPaymentsReceived = orders.reduce(
        (acc, order) => acc + (order.totalAmount || 0),
        0
      );

      const completedOrders = orders.filter(
        (order) => order.status === "completed"
      );
      const rejectedOrders = orders.filter(
        (order) => order.status === "rejected"
      );

      const ordersToday = orders.filter(
        (order) => 
          
          parseDate(order.date).toDateString() === new Date().toDateString()
      );

      const ordersThisWeek = orders.filter(
        (order) => parseDate(order.date) >= startOfWeek
      );
      const ordersThisMonth = orders.filter(
        (order) => parseDate(order.date) >= startOfMonth
      );

      const ordersLastMonth = orders.filter((order) => {
        const orderDate = parseDate(order.date);
        return orderDate >= startOfLastMonth && orderDate <= endOfLastMonth;
      });

      const dayNames = [
        "Sunday",
        "Monday",
        "Tuesday",
        "Wednesday",
        "Thursday",
        "Friday",
        "Saturday",
      ];

      return {
        totalOrders: {
          length: totalOrders,
          value: totalPaymentsReceived,
        },
        completedOrders: {
          length: completedOrders.length,
          value: completedOrders.reduce(
            (acc, order) => acc + order.totalAmount,
            0
          ),
        },
        rejectedOrders: {
          length: rejectedOrders.length,
          value: rejectedOrders.reduce(
            (acc, order) => acc + order.totalAmount,
            0
          ),
        },
        ordersToday: {
          length: ordersToday.length,
          value: ordersToday.reduce((acc, order) => acc + (order.totalAmount || 0), 0),
          dayName: dayNames[today.getDay()],
        },

        ordersThisWeek: {
          length: ordersThisWeek.length,
          value: ordersThisWeek.reduce(
            (acc, order) => acc + order.totalAmount,
            0
          ),
        },
        ordersThisMonth: {
          length: ordersThisMonth.length,
          value: ordersThisMonth.reduce(
            (acc, order) => acc + order.totalAmount,
            0
          ),
          monthName: monthNames[today.getMonth()],
        },
        ordersLastMonth: {
          length: ordersLastMonth.length,
          value: ordersLastMonth.reduce(
            (acc, order) => acc + order.totalAmount,
            0
          ),
          monthName: monthNames[(today.getMonth() - 1 + 12) % 12],
        },
      };
    };
    const stats = calculateStats(Orders);
    setStats(stats);
  }, [Orders]);

  // CSV DATA
  useEffect(() => {
    const csvData = [
      {
        Statistic: "Total Payments Received",
        Value: stats.totalOrders.value,
      },
      {
        Statistic: "Total Funds in Wallet",
        Value: totalFunds.toFixed(2),
      },
      {
        Statistic: "Total Orders",
        Count: stats.totalOrders.length,
        Value: stats.totalOrders.value,
      },
      {
        Statistic: "Completed Orders",
        Count: stats.completedOrders.length,
        Value: stats.completedOrders.value,
      },
      {
        Statistic: "Rejected Orders",
        Count: stats.rejectedOrders.length,
        Value: stats.rejectedOrders.value,
      },
      {
        Statistic: `Orders Today (${stats.ordersToday.dayName})`,
        Count: stats.ordersToday.length,
        Value: stats.ordersToday.value,
      },
      {
        Statistic: "Orders This Week",
        Count: stats.ordersThisWeek.length,
        Value: stats.ordersThisWeek.value,
      },
      {
        Statistic: `Orders This Month (${stats.ordersThisMonth.monthName})`,
        Count: stats.ordersThisMonth.length,
        Value: stats.ordersThisMonth.value,
      },
      {
        Statistic: `Orders Last Month (${stats.ordersLastMonth.monthName})`,
        Count: stats.ordersLastMonth.length,
        Value: stats.ordersLastMonth.value,
      },
    ];
    setCsvData(csvData);
  }, [stats, totalFunds]);

// console.log('orders4API',Orders)
// console.log('orders4Store',Orders)

  return (
    <>
      <Container className="py-1">
        <h3 className="text-center text-primary">SUMMARY </h3>
        <hr />
        <h2>Finance </h2>
        <div className="table-responsive">
          <Table striped bordered hover>
            <thead>
              <tr>
                <th>Statistic</th>
                <th>Value</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Total Payments Received</td>
                <td>&cent; {stats.totalOrders.value?.toFixed(2)}</td>
              </tr>
              <tr>
                <td>Total Funds in Wallets</td>
                <td>&cent; {totalFunds?.toFixed(2)}</td>
              </tr>
            </tbody>
          </Table>
        </div>

        <h2>Orders</h2>
        <div className="table-responsive">
          <Table striped bordered hover>
            <thead>
              <tr>
                <th>Statistic</th>
                <th>Count</th>
                <th>Value</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Total Orders</td>
                <td>{stats.totalOrders.length}</td>
                <td>&cent; {stats.totalOrders.value?.toFixed(2)}</td>
              </tr>
              <tr>
                <td>Completed Orders</td>
                <td>{stats.completedOrders.length}</td>
                <td>&cent; {stats.completedOrders.value?.toFixed(2)}</td>
              </tr>
              <tr>
                <td>Rejected Orders</td>
                <td>{stats.rejectedOrders.length}</td>
                <td>&cent; {stats.rejectedOrders.value?.toFixed(2)}</td>
              </tr>
              <tr>
                <td>Orders for Today ({stats.ordersToday.dayName})</td>
                <td>{stats.ordersToday.length}</td>
                <td>&cent; {stats.ordersToday.value?.toFixed(2)}</td>
              </tr>
              <tr>
                <td>Orders this week</td>
                <td>{stats.ordersThisWeek.length}</td>
                <td>&cent; {stats.ordersThisWeek.value?.toFixed(2)}</td>
              </tr>
              <tr>
                <td>Orders this month ({stats.ordersThisMonth.monthName})</td>
                <td>{stats.ordersThisMonth.length}</td>
                <td>&cent; {stats.ordersThisMonth.value?.toFixed(2)}</td>
              </tr>
              <tr>
                <td>Orders last month ({stats.ordersLastMonth.monthName})</td>
                <td>{stats.ordersLastMonth.length}</td>
                <td>&cent; {stats.ordersLastMonth.value?.toFixed(2)}</td>
              </tr>
            </tbody>
          </Table>
        </div>
      </Container>

      <CSVLink
        className="btn btn-primary p-2 mb-5 text-center w-100"
        data={csvData}
        filename={"GhanaBundles-admin-stats.csv"}
      >
        Export to Excel
      </CSVLink>
    </>
  );
};

export default AdminStats;
